import React from 'react';
import { 
  HeartIcon, 
  UserGroupIcon, 
  TrophyIcon,
  SparklesIcon,
  AcademicCapIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const About = () => {
  const guidingPrinciples = [
    {
      icon: HeartIcon,
      title: "Community First",
      description: "To build a COMMUNITY which gives, gives and gives."
    },
    {
      icon: SparklesIcon,
      title: "Have Fun",
      description: "To have FUN."
    },
    {
      icon: UserGroupIcon,
      title: "Athletics for All",
      description: "To promote athletics as a sport for ALL (all ages, both the young and the old and also people with special needs)."
    },
    {
      icon: AcademicCapIcon,
      title: "Movement Skills",
      description: "Athletics as a MOVEMENT skill (sprinting, throwing and jumping) which is applicable to all sports."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-oldham-blue/20 to-oldham-red/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-oldham-gold">Oldham Athletics</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              A community-driven sports club founded in 2019 with a mission to inspire athletes 
              and foster a sense of belonging through the shared love for athletics.
            </p>
          </div>
        </div>
      </div>

      {/* Our Story Section */}
      <div className="py-20 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-8">
                Our <span className="text-oldham-gold">Story</span>
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>
                  What began as a small group of passionate volunteers and athletes has evolved into a thriving club, 
                  welcoming individuals of all backgrounds and abilities. Our philosophy is grounded in resilience, 
                  teamwork, and a steadfast belief that the best is yet to be.
                </p>
                <p>
                  From our very first event, a family-oriented park run in 2019, to competing on international stages 
                  like the Tokyo Olympic Stadium, Oldham Athletics has always been about more than just competition. 
                  We celebrate personal growth, lifelong friendships, and the journey of every individual who joins our team.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-oldham-blue/20 to-oldham-red/20 rounded-2xl p-8 backdrop-blur-sm border border-gray-700/50">
                <div className="text-center">
                  <img 
                    src="https://firebasestorage.googleapis.com/v0/b/oldham-dashboard1.firebasestorage.app/o/branding%2FWAC_Pic.jpeg?alt=media" 
                    alt="Oldham Athletics at Tokyo Olympic Stadium" 
                    className="w-full h-64 object-cover rounded-lg shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guiding Principles Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">
              Our <span className="text-oldham-gold">Guiding Principles</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The values that drive our community and shape our approach to athletics
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {guidingPrinciples.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <div key={index} className="group">
                  <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 h-full transition-all duration-300 group-hover:border-oldham-gold/50 group-hover:bg-gray-800/70">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-oldham-gold/20 to-oldham-blue/20 rounded-full mb-6 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-8 h-8 text-oldham-gold" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-4">{principle.title}</h3>
                      <p className="text-gray-300 leading-relaxed">{principle.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>


      {/* Our Programs Section */}
      {/* <div className="py-20 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">
              Our <span className="text-oldham-gold">Programs</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Tailored programs to meet the needs of athletes at different stages of their journey
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8">
              <UserGroupIcon className="w-12 h-12 text-oldham-blue mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Junior Athletes (13+)</h3>
              <p className="text-gray-300 leading-relaxed">
                Structured training to nurture talent while ensuring holistic development. 
                Building strong foundations for future success.
              </p>
            </div>
            
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8">
              <TrophyIcon className="w-12 h-12 text-oldham-gold mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">National Athletes</h3>
              <p className="text-gray-300 leading-relaxed">
                High-performance pathways to compete at the highest levels, including 
                preparation for events like the SEA Games.
              </p>
            </div>
            
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8">
              <HeartIcon className="w-12 h-12 text-oldham-red mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Community Runners</h3>
              <p className="text-gray-300 leading-relaxed">
                Inclusivity for those who run for health, fitness, or fun. 
                Everyone is welcome in our vibrant community.
              </p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Vision Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-oldham-gold/10 to-oldham-blue/10 rounded-2xl p-8 backdrop-blur-sm border border-gray-700/50">
                <GlobeAltIcon className="w-16 h-16 text-oldham-gold mb-6" />
                <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                <p className="text-gray-300 leading-relaxed">
                  At Oldham Athletics, we are driven by a spirit of collaboration and perseverance. 
                  From overcoming challenges such as the COVID-19 pandemic to building strong relationships 
                  within our community, we continue to grow stronger together.
                </p>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-white mb-8">
                The Best is Yet to <span className="text-oldham-gold">Be</span>
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>
                  Guided by passionate coaches, dedicated volunteers, and a shared commitment to excellence, 
                  we inspire each other to push boundaries and achieve new heights.
                </p>
                <p>
                  Whether you are an aspiring athlete, an experienced competitor, or someone looking for a 
                  supportive community, Oldham Athletics welcomes you to join us. Together, we will continue 
                  to celebrate the transformative power of athletics and create memories that last a lifetime.
                </p>
                <div className="mt-8">
                  <p className="text-2xl font-bold text-oldham-gold">
                    The best is truly yet to be!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-20 bg-gradient-to-r from-oldham-blue/20 to-oldham-red/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Join Our <span className="text-oldham-gold">Community</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Ready to be part of something greater? Whether you're taking your first steps into athletics 
            or aiming for the podium, we're here to support your journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/programmes"
              className="bg-oldham-gold text-gray-900 font-semibold py-4 px-8 rounded-lg hover:bg-oldham-gold/90 transition-all duration-300 transform hover:scale-105"
            >
              Explore Programmes
            </a>
            <a
              href="/contact"
              className="border-2 border-oldham-gold text-oldham-gold font-semibold py-4 px-8 rounded-lg hover:bg-oldham-gold hover:text-gray-900 transition-all duration-300"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
